import { authFetch, readJSON } from './auth';

let scriptLoading = null;

export function loadRazorpayCheckout() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      scriptLoading = null;
      resolve(false);
    };
    document.body.appendChild(s);
  });
  return scriptLoading;
}

/**
 * Run a full Razorpay checkout flow for the given package.
 * Returns { granted, alreadyProcessed, payment_id } on success.
 * Throws on failure or modal close.
 */
export async function payWithRazorpay({ packageId, user, onSuccessRedirect }) {
  const ok = await loadRazorpayCheckout();
  if (!ok) throw new Error('Could not load Razorpay. Please retry.');

  // 1. Backend creates the order (server-defined amount).
  const orderRes = await authFetch('/api/billing/razorpay/order', {
    method: 'POST',
    body: JSON.stringify({ package_id: packageId }),
  });
  const order = await readJSON(orderRes);
  if (!orderRes.ok) {
    throw new Error(order?.detail || order?.message || 'Could not create order');
  }
  if (!order?.order_id) {
    throw new Error('Server did not return an order id');
  }

  // 2. Open the Razorpay modal.
  // Razorpay's checkout.js makes its OWN internal fetches that occasionally
  // get intercepted by analytics scripts (PostHog session-recording, rrweb)
  // and surface as "Failed to execute 'clone' on 'Response'" errors. These are
  // harmless to the actual checkout — the modal still opens and payments work.
  // We catch them inside the Promise so they don't propagate to setError.
  return new Promise((resolve, reject) => {
    let rzp;
    try {
      rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Facade Lighting Studio',
        description: order.package_name,
        order_id: order.order_id,
        prefill: {
          email: user?.email || '',
          name: user?.name || '',
        },
        theme: { color: '#f59e0b' },
        modal: {
          ondismiss: () => reject(new Error('Payment cancelled')),
        },
        handler: async (response) => {
          try {
            // 3. Backend verifies signature + grants the package.
            const verifyRes = await authFetch('/api/billing/razorpay/verify', {
              method: 'POST',
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const data = await readJSON(verifyRes);
            if (!verifyRes.ok) {
              throw new Error(data?.detail || data?.message || 'Verification failed');
            }
            if (onSuccessRedirect) {
              window.location.href = `${onSuccessRedirect}?razorpay_payment_id=${response.razorpay_payment_id}&granted=${data.granted_now ? 1 : 0}`;
            }
            resolve({
              granted: data.granted_now,
              alreadyProcessed: data.already_processed,
              payment_id: response.razorpay_payment_id,
            });
          } catch (e) {
            reject(e);
          }
        },
      });
    } catch (e) {
      // Constructor itself threw — rare, but real failure
      reject(e);
      return;
    }

    // rzp.open() can synchronously emit clone-related errors from analytics
    // interceptors when Razorpay's iframe makes its own internal requests.
    // Swallow those — the modal is already loading and the user can complete
    // payment normally. Only rethrow if open() itself fails to launch.
    try {
      rzp.open();
    } catch (e) {
      const msg = (e && e.message) || '';
      if (
        msg.includes("clone") ||
        msg.includes('Response body is already used')
      ) {
        // Benign analytics-interceptor noise — modal still opens.
        // eslint-disable-next-line no-console
        console.warn('[razorpay] Ignored analytics interceptor error:', msg);
      } else {
        reject(e);
      }
    }
  });
}
